import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import importAsset from 'lumen/helpers/import-asset';
import type Highlight from 'lumen/models/roi-trophies/highlight';
import type { AutopilotAnniversaryData } from 'lumen/models/roi-trophies/highlight';
import lottieAnimation from 'lumen/modifiers/lottie-animation';

import GeneralRoiTrophiesContentTrophyModalLayout from './trophy-modal-layout';

interface GeneralRoiTrophiesContentAutopilotAnniversarySignature {
  Args: {
    highlight: Highlight;
  };
}

export default class GeneralRoiTrophiesContentAutopilotAnniversary extends Component<GeneralRoiTrophiesContentAutopilotAnniversarySignature> {
  @service declare intl: Services['intl'];

  get body(): string {
    const pricePushes = this.intl.formatNumber(this.data.price_pushes);
    const reservations = this.intl.formatNumber(this.data.pickup);

    const { months } = this.data;

    if (!months || months < 6) {
      return this.intl.t('roi_trophies.autopilot_anniversary.body_1_month', {
        price_pushes: pricePushes,
        reservations,
      });
    }

    if (months < 12) {
      return this.intl.t('roi_trophies.autopilot_anniversary.body_6_months', {
        price_pushes: pricePushes,
        reservations,
      });
    }

    if (months < 24) {
      return this.intl.t('roi_trophies.autopilot_anniversary.body_1_year', {
        price_pushes: pricePushes,
        reservations,
      });
    }

    return this.intl.t('roi_trophies.autopilot_anniversary.body_multi_year', {
      price_pushes: pricePushes,
      reservations,
      years: this.years,
    });
  }

  get data(): AutopilotAnniversaryData {
    return this.args.highlight.highlightData as AutopilotAnniversaryData;
  }

  get title(): string {
    const { months } = this.data;

    if (!months || months < 12) {
      return this.intl.t('roi_trophies.autopilot_anniversary.title_months', {
        months: months ?? 1,
      });
    }

    return this.intl.t('roi_trophies.autopilot_anniversary.title_years', {
      years: this.years,
    });
  }

  get years(): number {
    return Math.floor((this.data.months ?? 0) / 12);
  }

  <template>
    <GeneralRoiTrophiesContentTrophyModalLayout>
      <:content>
        <div class="flex flex-col items-center gap-300">
          <div
            class="heading-1"
            data-test-id="autopilot-anniversary-title"
          >{{this.title}}</div>
          <p
            class="text-300--regular text-neutral-subdued"
            data-test-id="autopilot-anniversary-body"
          >{{this.body}}</p>
        </div>
      </:content>
      <:animation>
        <div
          {{lottieAnimation
            (importAsset "animations/roi-trophies/autopilot.json")
            false
          }}
        />
      </:animation>
    </GeneralRoiTrophiesContentTrophyModalLayout>
  </template>
}
